/**
 * Množství použitého materiálu — shodně s Postgres `material_usage.quantity` NUMERIC(12,3).
 *
 * Jednotky katalogu (`components.jednotka`):
 * - `ks`, `m`, … → jednoduchá jednotka (nákup = spotřeba)
 * - `ks (2 m)` / `ks (6m)` → 1 kus = N metrů (obsah kusu; spotřeba i nákup = ks)
 * - `ks+m` → kus s délkou jako vstup (příjem = kusy × délka; sklad = kus s délkou jako `m`)
 *
 * Legacy `N ks` (dříve balení) se kanonizuje na plain `ks`. Zásoby byly vždy ve spotřebních
 * kusech — převod katalogu na `ks` nemění uložené množství.
 */

/** Katalogový kód: kus s délkou jako vstup na příjemce. */
export const PIECE_LENGTH_INPUT_JEDNOTKA = 'ks+m';

const MATERIAL_USAGE_QUANTITY_DECIMAL_PLACES = 3;

/** Legacy pack encoding `10 ks` / `6ks` — no longer a first-class purchase unit. */
const LEGACY_PACK_UNIT_RE = /^(\d+(?:[.,]\d+)?)ks$/;
const PIECE_CONTENT_RE = /^ks\((\d+(?:[.,]\d+)?)m\)$/;

export function roundMaterialUsageQuantity(value: number): number {
  if (!Number.isFinite(value)) return value;
  const p = 10 ** MATERIAL_USAGE_QUANTITY_DECIMAL_PLACES;
  return Math.round(value * p) / p;
}

/**
 * Normalizace jednotky z katalogu / Product (mezery, case, ^2/^3).
 * Lineární metry ≠ m² / m³.
 */
export function normalizeMaterialQuantityUnit(unit: string | null | undefined): string {
  return (unit ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\^2/g, '2')
    .replace(/\^3/g, '3');
}

/** True when catalog string is legacy pack encoding (`10 ks`). */
export function isLegacyPackUnitEncoding(unit: string | null | undefined): boolean {
  const u = normalizeMaterialQuantityUnit(unit);
  if (!u) return false;
  const m = LEGACY_PACK_UNIT_RE.exec(u);
  if (!m) return false;
  const n = parsePositiveNumber(m[1]);
  return n != null && n >= 1;
}

/**
 * `ks+m` / legacy alias `balení (ks + m)` — délka je vstup (ne pevný obsah `ks (N m)`).
 */
export function isPieceLengthInputUnit(unit: string | null | undefined): boolean {
  const u = normalizeMaterialQuantityUnit(unit);
  if (!u) return false;
  if (u === 'ks+m') return true;
  return u === 'baleni(ks+m)' || u === 'balení(ks+m)';
}

/**
 * Délkové materiály ve skladu: 1 WarehouseItem = 1 kus (role/spool) s `quantity` = délka.
 * Nesčítat do jedné SAP řádky; převod musí cílit konkrétní WarehouseItem.id.
 * Platí i pro `ks+m`. Neplatí pro `ks (2 m)` — to je kus s pevným obsahem, ne role.
 */
export function isWarehousePieceLengthUnit(unit: string | null | undefined): boolean {
  const u = normalizeMaterialQuantityUnit(unit);
  if (!u || u === 'm2' || u === 'm²' || u === 'm3' || u === 'm³') return false;
  if (isPieceLengthInputUnit(unit)) return true;
  return u === 'm' || u === 'bm' || u === 'mb' || u === 'm.b.' || u === 'metr' || u === 'metry';
}

/**
 * Zásoby UI: rozbalovat jen skutečné `m` (uložená MJ).
 * Kusové (`ks`) a leftover hybridy (`ks+m`, `ks (N m)`) zůstanou jeden řádek se součtem.
 */
export function shouldExpandWarehouseStockPieces(
  unit: string | null | undefined,
  pieceCount: number,
): boolean {
  return pieceCount >= 1 && normalizeMaterialQuantityUnit(unit) === 'm';
}

/** Druh jednotky v katalogu (balení / pack už není). */
export type MaterialUnitKind = 'simple' | 'piece_length' | 'piece_length_input';

/**
 * Strukturovaný výklad `components.jednotka`.
 * Zásoba i nákup/příjem jsou ve stejné MJ (ks / m) — bez nákupních balení.
 */
export interface MaterialUnitInfo {
  /** Původní řetězec (trim); u legacy `N ks` kanonicky `ks`. */
  raw: string;
  kind: MaterialUnitKind;
  /** Spotřební MJ (`ks`, `m`, …). */
  usageUnit: string;
  /** Nákupní MJ (= usage; dříve u pack = balení). */
  purchaseUnit: string;
  /** Vždy 1 — legacy packSize už se nepoužívá. */
  packSize: number;
  /** Metry na 1 kus u `ks (N m)`; jinak null. */
  pieceContentMeters: number | null;
  /** Compact normalizace pro porovnání. */
  normalized: string;
}

function parsePositiveNumber(raw: string): number | null {
  const n = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Parsuje katalogovou jednotku. Legacy `N ks` → plain `ks`.
 */
export function parseMaterialUnit(unit: string | null | undefined): MaterialUnitInfo {
  const raw = (unit ?? '').trim();
  const normalized = normalizeMaterialQuantityUnit(unit);

  if (isPieceLengthInputUnit(unit)) {
    return {
      raw: raw || PIECE_LENGTH_INPUT_JEDNOTKA,
      kind: 'piece_length_input',
      usageUnit: 'm',
      purchaseUnit: PIECE_LENGTH_INPUT_JEDNOTKA,
      packSize: 1,
      pieceContentMeters: null,
      normalized: 'ks+m',
    };
  }

  // Legacy pack encoding → plain pieces (no balení purchase model).
  if (isLegacyPackUnitEncoding(unit)) {
    return {
      raw: 'ks',
      kind: 'simple',
      usageUnit: 'ks',
      purchaseUnit: 'ks',
      packSize: 1,
      pieceContentMeters: null,
      normalized: 'ks',
    };
  }

  const pieceMatch = PIECE_CONTENT_RE.exec(normalized);
  if (pieceMatch) {
    const meters = parsePositiveNumber(pieceMatch[1]);
    if (meters != null) {
      return {
        raw,
        kind: 'piece_length',
        usageUnit: 'ks',
        purchaseUnit: 'ks',
        packSize: 1,
        pieceContentMeters: meters,
        normalized,
      };
    }
  }

  const simple = raw || normalized;
  return {
    raw,
    kind: 'simple',
    usageUnit: simple,
    purchaseUnit: simple,
    packSize: 1,
    pieceContentMeters: null,
    normalized,
  };
}

/** @deprecated Pack MJ zrušeno — vždy false. */
export function isPackagedMaterialUnit(_unit: string | null | undefined): boolean {
  return false;
}

export function isPieceContentLengthUnit(unit: string | null | undefined): boolean {
  return parseMaterialUnit(unit).kind === 'piece_length';
}

/**
 * Kanonický katalogový řetězec (legacy `N ks` → `ks`).
 */
export function canonicalizeMaterialJednotka(unit: string | null | undefined): string {
  if (isLegacyPackUnitEncoding(unit)) return 'ks';
  const info = parseMaterialUnit(unit);
  if (info.kind === 'piece_length_input') return PIECE_LENGTH_INPUT_JEDNOTKA;
  if (info.kind === 'piece_length' && info.pieceContentMeters != null) {
    const m = info.pieceContentMeters;
    const whole = Number.isInteger(m) ? String(m) : String(m);
    return `ks (${whole} m)`;
  }
  return (info.raw || info.usageUnit || 'ks').trim() || 'ks';
}

/** Složí katalogový řetězec jednotky (zařazení / editace). */
export function composeMaterialJednotka(input: {
  kind: MaterialUnitKind | 'pack';
  /** Pro `simple` — např. ks, m, m2. */
  simpleUnit?: string | null;
  /** Ignorováno — pack MJ zrušeno. */
  packSize?: number | null;
  pieceContentMeters?: number | null;
}): string {
  // Legacy callers that still pass kind:'pack' → plain ks.
  if (input.kind === 'pack') {
    return 'ks';
  }
  if (input.kind === 'piece_length') {
    const m = Number(input.pieceContentMeters);
    if (Number.isFinite(m) && m > 0) {
      const whole = Number.isInteger(m) ? String(m) : String(m);
      return `ks (${whole} m)`;
    }
    return 'ks';
  }
  if (input.kind === 'piece_length_input') {
    return PIECE_LENGTH_INPUT_JEDNOTKA;
  }
  const simple = String(input.simpleUnit ?? '').trim() || 'ks';
  if (isLegacyPackUnitEncoding(simple)) return 'ks';
  return simple;
}

/**
 * Nákupní množství → spotřební. Pack MJ zrušeno — identity.
 */
export function purchaseToUsageQuantity(
  purchaseQty: number,
  _unit?: string | null,
): number {
  if (!Number.isFinite(purchaseQty)) return purchaseQty;
  return roundMaterialUsageQuantity(purchaseQty);
}

/**
 * Spotřební množství → nákupní. Pack MJ zrušeno — identity.
 */
export function usageToPurchaseQuantity(
  usageQty: number,
  _unit?: string | null,
): number {
  if (!Number.isFinite(usageQty)) return usageQty;
  return roundMaterialUsageQuantity(usageQty);
}

/**
 * Zaokrouhlení jednotkové ceny (material_usage / FIFO — 4 desetinná místa).
 */
export function roundMaterialUnitPrice(value: number): number {
  if (!Number.isFinite(value)) return value;
  return Math.round(value * 10000) / 10000;
}

/**
 * Nákupní jednotková cena → spotřební. Pack MJ zrušeno — identity (Kč/ks = Kč/MJ).
 */
export function purchaseToUsageUnitPrice(
  purchaseUnitPrice: number,
  _unit?: string | null,
): number {
  if (!Number.isFinite(purchaseUnitPrice)) return purchaseUnitPrice;
  return roundMaterialUnitPrice(purchaseUnitPrice);
}

/**
 * Vstupní množství → skladová (spotřební) jednotka.
 * Příjem/odebrání zadává uživatel přímo v ks / m.
 */
export function toWarehouseStorageQuantity(
  qty: number,
  unit: string | null | undefined,
  inputMode: 'purchase' | 'usage',
): number {
  if (!Number.isFinite(qty)) return qty;
  if (inputMode === 'usage') return roundMaterialUsageQuantity(qty);
  return purchaseToUsageQuantity(qty, unit);
}

/** Ekvivalent metrů pro `ks (N m)` při množství v ks. */
export function usageQuantityToMeters(
  usageQty: number,
  unit: string | null | undefined,
): number | null {
  const { pieceContentMeters } = parseMaterialUnit(unit);
  if (pieceContentMeters == null || !Number.isFinite(usageQty)) return null;
  return roundMaterialUsageQuantity(usageQty * pieceContentMeters);
}

export type MaterialQuantityDisplayMode = 'usage' | 'purchase' | 'stock';

function formatQtyNumber(qty: number): string {
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(qty);
}

/**
 * Popisek MJ podle režimu (spotřeba / nákup / katalogový raw).
 * Nikdy nevrací „balení“ pro kusové materiály.
 */
export function formatMaterialUnitLabel(
  unit: string | null | undefined,
  mode: MaterialQuantityDisplayMode | 'catalog' = 'usage',
): string {
  const info = parseMaterialUnit(unit);
  if (mode === 'catalog') {
    if (info.kind === 'piece_length_input') return PIECE_LENGTH_INPUT_JEDNOTKA;
    if (isLegacyPackUnitEncoding(unit)) return 'ks';
    return info.raw || info.usageUnit || '—';
  }
  if (mode === 'purchase') {
    if (info.kind === 'piece_length' && info.pieceContentMeters != null) {
      return `ks (${info.pieceContentMeters} m)`;
    }
    if (info.kind === 'piece_length_input') return 'ks + m';
    return info.purchaseUnit || info.raw || '—';
  }
  if (mode === 'stock') {
    if (info.kind === 'piece_length') return 'ks';
    if (info.kind === 'piece_length_input') return 'm';
    return info.usageUnit || info.raw || '—';
  }
  // usage
  if (info.kind === 'piece_length' && info.pieceContentMeters != null) {
    return `ks (${info.pieceContentMeters} m)`;
  }
  if (info.kind === 'piece_length_input') return 'm';
  return info.usageUnit || info.raw || '—';
}

/**
 * Formát množství s jednotkou.
 * - usage / purchase: `5 ks` / `12 m` (bez balení)
 * - stock: `3 ks (6 m)` u piece_length
 */
export function formatMaterialQuantityLabel(
  qty: number,
  unit: string | null | undefined,
  mode: MaterialQuantityDisplayMode = 'usage',
): string {
  if (!Number.isFinite(qty)) return '—';
  const info = parseMaterialUnit(unit);
  const n = formatQtyNumber(qty);

  if (mode === 'purchase') {
    const label = formatMaterialUnitLabel(unit, 'purchase');
    return label && label !== '—' ? `${n} ${label}` : n;
  }

  if (mode === 'stock') {
    if (info.kind === 'piece_length' && info.pieceContentMeters != null) {
      const meters = usageQuantityToMeters(qty, unit);
      if (meters != null) return `${n} ks (${formatQtyNumber(meters)} m)`;
    }
    const u = info.usageUnit || info.raw;
    return u ? `${n} ${u}` : n;
  }

  // usage
  if (info.kind === 'piece_length' && info.pieceContentMeters != null) {
    return `${n} ks`;
  }
  const u = info.usageUnit || info.raw;
  return u ? `${n} ${u}` : n;
}

/** AutoCRM Product.measureUnit — skladová MJ. */
export function resolveWarehouseMeasureUnit(unit: string | null | undefined): string {
  const info = parseMaterialUnit(unit);
  if (info.kind === 'piece_length') return 'ks';
  if (info.kind === 'piece_length_input') return 'm';
  return info.usageUnit || 'ks';
}

/**
 * Jednotka pro zobrazení spotřeby (Info / picker / hub).
 * Katalog je SSOT — u `m` nikdy nepadat na uložené `ks` (usage řádky často jednotku vynechají).
 * Legacy `N ks` → `ks`. Pin kusu (`warehouseItemId`) je jen u metrů.
 */
export function preferredUsageJednotka(
  stored?: string | null,
  catalog?: string | null,
  warehouseItemId?: string | null,
): string {
  const catalogTrim = (catalog ?? '').trim();
  if (catalogTrim) {
    if (isLegacyPackUnitEncoding(catalogTrim)) return 'ks';
    return catalogTrim;
  }
  const storedTrim = (stored ?? '').trim();
  const hasPiece = Boolean((warehouseItemId ?? '').trim());
  if (hasPiece) {
    if (!storedTrim) return 'm';
    if (isLegacyPackUnitEncoding(storedTrim)) return 'm';
    const usage = formatMaterialUnitLabel(storedTrim, 'usage');
    if (usage === 'ks') return 'm';
    return storedTrim;
  }
  if (isLegacyPackUnitEncoding(storedTrim)) return 'ks';
  return storedTrim || 'ks';
}

export interface PurchasePackQuantityLine {
  sapCode?: string | null;
  /** Množství v ks / m (dříve u pack = počet balení). */
  quantity: number;
  /** Katalogová `jednotka`. */
  unit?: string | null;
}

/**
 * Pack MJ zrušeno — validace balení vždy OK (`null`).
 * Zachováno pro call-site kompatibilitu.
 */
export function wholePurchasePacksError(
  _lines: ReadonlyArray<PurchasePackQuantityLine>,
): string | null {
  return null;
}

/**
 * Stejná validace jako {@link wholePurchasePacksError}; při chybě hodí `Error`.
 */
export function assertWholePurchasePacks(
  lines: ReadonlyArray<PurchasePackQuantityLine>,
): void {
  const err = wholePurchasePacksError(lines);
  if (err) throw new Error(err);
}

/**
 * Normalizace nákupního množství v UI. Pack MJ zrušeno — ponechá finite qty.
 */
export function normalizePurchaseQuantityInput(
  qty: number,
  _unit?: string | null,
): number {
  return qty;
}

